import { useEffect, useMemo, useState } from "react";
import { useDeleteAsk, deletionRequested } from "@/lib/deleteApproval";
import {
  getMerchStock, listMerchMovements, addMerchStock, receiveMerchStock, countMerchStock,
  setMerchLowLevel, removeMerchStock, merchKey,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PackagePlus, ClipboardCheck, Trash2, Package } from "lucide-react";

// Stock per gym for each Merch item (from Settings → Revenue options) and size.
// Sales in All Sales and "T-shirt given" on Accountability take stock automatically.
export default function MerchStock() {
  const askDelete = useDeleteAsk();
  const { user, effectiveRole, activeFacility } = useAuth();
  const canEdit = atLeast(effectiveRole || user?.role, "manager");
  const [data, setData] = useState({ items: [], stock: [] });
  const [moves, setMoves] = useState([]);
  const [dialog, setDialog] = useState(null); // {type: "add"|"receive"|"count", item?, row?}

  const load = () => {
    getMerchStock().then(setData).catch(() => setData({ items: [], stock: [] }));
    listMerchMovements({ limit: 60 }).then(setMoves).catch(() => setMoves([]));
  };
  useEffect(load, [activeFacility?.id]);

  const byItem = useMemo(() => {
    const out = data.items.map((name) => ({ name, rows: data.stock.filter((r) => merchKey(r.item) === merchKey(name)) }));
    // Stock lines for an item since removed from the Merch list still show, so nothing is hidden.
    data.stock.forEach((r) => {
      if (!out.some((g) => merchKey(g.name) === merchKey(r.item))) out.push({ name: r.item, rows: [r] });
    });
    return out;
  }, [data]);

  const remove = async (row) => {
    const reason = askDelete("this stock line");
    if (reason === null) return;
    try {
      if (deletionRequested(await removeMerchStock(row.id, reason))) return;
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove");
    }
  };

  return (
    <div className="space-y-6" data-testid="merch-stock-page">
      <div>
        <div className="eyebrow">Merchandise</div>
        <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Merch stock · {activeFacility?.name}</h2>
        <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
          Stock goes down when merch is sold in <span className="text-zinc-200">All Sales</span> and when a T-shirt is
          ticked as given on <span className="text-zinc-200">Accountability</span>, and goes back up if that is undone.
          It can&apos;t go below zero. Items with no sizes added here aren&apos;t tracked and sell as before.
          {!canEdit && " Only managers can change stock."}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {byItem.map((g) => (
          <div key={g.name} className="card-surface p-5 space-y-3" data-testid={`merch-item-${merchKey(g.name)}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-zinc-500" />
                <h3 className="font-semibold">{g.name}</h3>
                {!g.rows.length && <span className="text-[11px] rounded-full border border-zinc-700 px-2 py-0.5 text-zinc-500">Not tracked</span>}
              </div>
              {canEdit && (
                <Button size="sm" variant="outline" className="h-8 rounded-full border-[#262626]"
                  onClick={() => setDialog({ type: "add", item: g.name, sized: g.rows.length ? !!g.rows[0].variant : null })}
                  data-testid={`merch-add-${merchKey(g.name)}`}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> {g.rows.length ? (g.rows[0].variant ? "Size" : "Line") : "Start tracking"}
                </Button>
              )}
            </div>
            {g.rows.length > 0 && (
              <div className="divide-y divide-[#1f1f1f] rounded-lg border border-[#1f1f1f]">
                {g.rows.map((r) => {
                  const low = r.on_hand <= (r.low_at ?? 0);
                  return (
                    <div key={r.id} className="flex items-center gap-3 px-3 py-2" data-testid={`merch-row-${r.id}`}>
                      <div className="w-16 text-sm text-zinc-300">{r.variant || "—"}</div>
                      <div className={`flex-1 text-lg font-semibold tabular-nums ${r.on_hand === 0 ? "text-rose-400" : low ? "text-amber-300" : ""}`}>
                        {r.on_hand}
                        {r.on_hand === 0 ? <span className="ml-2 text-xs font-normal">out of stock</span>
                          : low ? <span className="ml-2 text-xs font-normal">low</span> : null}
                      </div>
                      {canEdit && (
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" className="h-8 px-2" title="Stock received"
                            onClick={() => setDialog({ type: "receive", row: r })} data-testid={`merch-receive-${r.id}`}>
                            <PackagePlus className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 px-2" title="Correct the count"
                            onClick={() => setDialog({ type: "count", row: r })} data-testid={`merch-count-${r.id}`}>
                            <ClipboardCheck className="h-4 w-4" />
                          </Button>
                          {r.on_hand === 0 && (
                            <Button size="sm" variant="ghost" className="h-8 px-2 text-zinc-500 hover:text-rose-400" title="Remove this line"
                              onClick={() => remove(r)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        {!byItem.length && <div className="text-sm text-zinc-500">No merch items yet. Add them under Settings → Revenue options → Merch.</div>}
      </div>

      <div className="card-surface overflow-hidden">
        <div className="px-5 py-3 border-b border-[#1f1f1f] font-semibold">Recent stock changes</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500">
              <tr className="text-left">
                <th className="px-5 py-2 font-medium">When</th><th className="px-3 py-2 font-medium">Item</th>
                <th className="px-3 py-2 font-medium text-right">Change</th><th className="px-3 py-2 font-medium text-right">Left</th>
                <th className="px-3 py-2 font-medium">Why</th><th className="px-3 py-2 font-medium">By</th>
              </tr>
            </thead>
            <tbody>
              {moves.map((m) => (
                <tr key={m.id} className="border-t border-[#141414]">
                  <td className="px-5 py-2 text-zinc-400 whitespace-nowrap">{new Date(m.at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="px-3 py-2">{m.item}{m.variant ? ` (${m.variant})` : ""}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${m.change < 0 ? "text-rose-300" : "text-emerald-300"}`}>{m.change > 0 ? `+${m.change}` : m.change}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{m.balance_after}</td>
                  <td className="px-3 py-2 text-zinc-300">{m.reason_label}{m.ref_label ? ` · ${m.ref_label}` : ""}{m.note ? <span className="text-zinc-500"> · {m.note}</span> : null}</td>
                  <td className="px-3 py-2 text-zinc-400">{m.by}</td>
                </tr>
              ))}
              {!moves.length && <tr><td colSpan={6} className="px-5 py-6 text-center text-zinc-500">No stock changes yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {dialog && <StockDialog dialog={dialog} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />}
    </div>
  );
}

function StockDialog({ dialog, onClose, onDone }) {
  const { type, row, item, sized } = dialog;
  const [variant, setVariant] = useState("");
  const [qty, setQty] = useState(type === "count" ? String(row.on_hand) : "");
  const [lowAt, setLowAt] = useState(String(row?.low_at ?? 2));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const label = row ? `${row.item}${row.variant ? ` (${row.variant})` : ""}` : item;
  const title = { add: `Add ${item} to stock`, receive: `Stock received · ${label}`, count: `Correct the count · ${label}` }[type];

  const submit = async (e) => {
    e.preventDefault();
    const n = parseInt(qty, 10);
    if (Number.isNaN(n) || n < 0) return toast.error("Enter a number of 0 or more");
    setBusy(true);
    try {
      if (type === "add") await addMerchStock({ item, variant: variant.trim(), opening: n, low_at: parseInt(lowAt, 10) || 0 });
      if (type === "receive") await receiveMerchStock(row.id, n, note);
      if (type === "count") {
        await countMerchStock(row.id, n, note);
        if (String(row.low_at) !== lowAt) await setMerchLowLevel(row.id, parseInt(lowAt, 10) || 0);
      }
      toast.success("Saved");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] max-w-md">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="merch-stock-form">
          {type === "add" && sized !== false && (
            <div className="space-y-1.5">
              <Label>Size / variant{sized === null ? " (leave blank if this item has no sizes)" : ""}</Label>
              <Input value={variant} onChange={(e) => setVariant(e.target.value)} placeholder="e.g. M" autoFocus
                className="bg-[#0a0a0a] border-[#262626]" data-testid="merch-variant-input" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{{ add: "How many in stock now", receive: "How many arrived", count: "Actual count now" }[type]}</Label>
            <Input type="number" min={0} value={qty} onChange={(e) => setQty(e.target.value)} required
              className="bg-[#0a0a0a] border-[#262626]" data-testid="merch-qty-input" />
          </div>
          {type !== "receive" && (
            <div className="space-y-1.5">
              <Label>Show as low at</Label>
              <Input type="number" min={0} value={lowAt} onChange={(e) => setLowAt(e.target.value)} className="bg-[#0a0a0a] border-[#262626]" />
            </div>
          )}
          {type !== "add" && (
            <div className="space-y-1.5">
              <Label>{type === "count" ? "Why is it changing?" : "Note (optional)"}</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} required={type === "count"}
                placeholder={type === "count" ? "e.g. monthly count, damaged" : "e.g. invoice number"}
                className="bg-[#0a0a0a] border-[#262626]" data-testid="merch-note-input" />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-500" data-testid="merch-save-btn">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
